import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';

interface TextFieldProps {
  className: string;
  classNameInputParent?: string;
  classNameInput?: string;
  text: string;
  inputValue: string;
  placeholderText: string;
  state?: 'default';
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onIconClick?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  errorState?: boolean;
  dataTestId?: string;
  'data-qa-selector'?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  className,
  classNameInputParent,
  classNameInput,
  text = 'Text',
  inputValue = 'Button',
  placeholderText = '',
  disabled = false,
  readOnly = false,
  type = 'text',
  onChange,
  onKeyDown,
  icon: Icon,
  onIconClick,
  onBlur,
  autoFocus,
  errorState,
  dataTestId,
  'data-qa-selector': dataQaSelector,
}) => {
  // Generate unique ID for label/input association
  // Always generated for accessibility - even without label, ID is useful for other purposes
  const inputId = useId();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {text && (
        <label htmlFor={inputId} className="text-button-sm font-medium text-neutral-500">
          {text}
        </label>
      )}

      <div className={cn('relative max-w-[160px]', classNameInputParent)}>
        <Input
          id={inputId}
          {...(dataTestId && { 'data-testid': dataTestId })}
          data-qa-selector={dataQaSelector}
          className={cn(`w-full`, classNameInput)}
          placeholder={placeholderText}
          type={type}
          value={inputValue}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          autoFocus={autoFocus}
          errorState={errorState}
        />

        {Icon && (
          <div className="absolute inset-y-0 right-1 flex items-center">
            <IconSimpleButton
              icon={Icon}
              size={24}
              onClick={onIconClick}
              data-qa-selector={dataQaSelector ? `${dataQaSelector}-icon-button` : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};
